import { useState } from 'react';
import {
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Icon, type IconName } from '@/components/Icon';
import { AccountAvatar, AccountSheet, AccountSwitcher } from '@/components/AccountSwitcher';
import { BottomNav } from '@/components/BottomNav';
import { Drawer } from '@/components/Drawer';
import { FabSlot } from '@/components/Fab';
import { MailboxTree } from '@/components/MailboxTree';
import { MAIN_CONTENT_ID, SkipLink } from '@/components/SkipLink';
import { UNIFIED_PATH, UNIFIED_SEGMENT } from '@/routes/mail/unifiedRoute';
import { OfflineIndicator, OfflineStatusDot } from '@/components/OfflineIndicator';
import { useOutboxFlusher } from '@/features/outbox';
import { useSentCopies } from '@/features/sent-copies';
import { CredentialBanner, ReauthBanner, ReauthPromptProvider } from '@/features/credential-notice';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { InstallPromptGate } from '@/components/InstallPromptGate';
import { DndIndicatorGate } from '@/components/DndIndicatorGate';
import { DraftRecovery } from '@/components/DraftRecovery';
import { ToastSlotContext } from '@/components/toastStack';
import { SendUndoSnackbar } from '@/features/send-undo';
import { useHandheld } from '@/hooks/useMediaQuery';
import { nextTheme, THEME_LABELS, useTheme, type ThemePreference } from '@/hooks/useTheme';
import { formatBytes } from '@/utils/format';
import { accountColorVar, useAccountContext } from './accounts';
import { cssVars } from '@/utils/css';
import { useAccountSearchParam } from './accountSearchParam';
import { COMPOSE_INTENT_NEW, COMPOSE_PARAM } from './composeIntent';
import { DrawerPanelProvider, useDrawer, useRegisteredDrawerPanel } from './drawer';
import { hasBottomNav, screenFor } from './screen';
import { useSession } from './session';
import { summarizeQuota, useAccountQuota } from './quota';
import { useFolderContext } from './folders';
import { PICKED_MAILBOX_STATE } from './preferences';
import { pluginRegistry } from '@/plugins';
import {
  AccountNotificationPoller,
  CalendarReminders,
  TaskReminders,
  useLiveUpdates,
} from '@/realtime';
import { usePrefetchRecipientDirectory } from '@/routes/mail/recipientDirectoryQuery';
import { SCOPE_PARAM, TRASH_PARAM } from '@/routes/mail/searchScope';
import { folderPath } from '@/features/scheduling/route';

/**
 * The application shell: top bar, sidebar, and a routed content region.
 *
 * This is the part the service worker precaches, so it paints before any
 * network request resolves — the "feels like a desktop app" requirement.
 *
 * Below 60rem it is the *handheld* shell instead (ADR 0048): a sticky app bar
 * with a search pill, a bottom bar for the products, a drawer for folders and
 * everything else, and no sidebar or status bar at all. Which one is drawn is
 * `useHandheld()`, mirrored onto `.shell[data-layout]`; which screen is up is
 * `screenFor()`, mirrored onto `.shell[data-screen]`. CSS keys on those two
 * attributes, so no rule has to repeat the media query to know where it is.
 */
/** The theme button shows the mode in force; its label names the next one. */
const THEME_ICON: Record<ThemePreference, IconName> = {
  light: 'sun',
  dark: 'moon',
  system: 'monitor',
};

export function AppShell() {
  return (
    <DrawerPanelProvider>
      {/* The connected-mailbox password modal (#192): anything in the shell
          that acts on a flagged mailbox asks through it. */}
      <ReauthPromptProvider>
        <Shell />
      </ReauthPromptProvider>
    </DrawerPanelProvider>
  );
}

function Shell() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  const { accounts, activeAccount } = useAccountContext();
  // `admin` only decides whether the entry is DRAWN. Hiding a link is not an
  // access control: the server re-checks admin.emails on every /admin/*
  // request and answers anyone else 404 (plan/13 §1).
  const { logout, account, admin } = useSession();
  const { inbox } = useFolderContext();
  // Warms the address book for the composer's suggestions, when the browser
  // is idle. See usePrefetchRecipientDirectory.
  usePrefetchRecipientDirectory(activeAccount?.id);
  // Drains the Outbox when the connection comes back, in one tab only
  // (issue #201). Mounted on the shell rather than on Mail, because a message
  // queued while the user was reading their calendar must still go out.
  useOutboxFlusher();
  // Copies of sent mail not in Sent yet (#84): a mailbox with no background
  // credential is retried only while someone is signed in, and this is where
  // the app asks, wherever in it the person is.
  useSentCopies(accounts);
  const location = useLocation();
  const handheld = useHandheld();
  const screen = screenFor(location.pathname, location.search);
  const drawer = useDrawer();
  const routeDrawerPanel = useRegisteredDrawerPanel();
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  /** Where the mail view's snackbar joins the toast stack (toastStack.ts). */
  const [toastSlot, setToastSlot] = useState<HTMLDivElement | null>(null);

  /**
   * The tab's single live-update stream (ADR 0002).
   *
   * Mounted here, once, for the whole tab: the stream is per *session* and
   * covers every account, and the server caps a session at eight of them, so
   * one per route would spend that cap on navigation. Every frame it receives
   * is turned into the same `CachePatch` values an optimistic mutation emits.
   */
  useLiveUpdates(account !== undefined);

  /**
   * The interim notification pollers (ADR 0021 §4.6), one per mailbox, each
   * leader-elected across windows so five open tabs do not poll five times.
   *
   * Every mailbox, not only the active one. The live stream only patches
   * caches, and a mailbox without background notifications has no push, so a
   * connected mailbox with a poller for the active account alone raised no
   * notification at all while this tab was open. A mailbox that is
   * not `connected` is skipped: its password no longer works, so there is
   * nothing to poll. Each poller is gated on its own saved preference.
   */
  const pollable = accounts.filter(
    (candidate) => candidate.isPrimary || candidate.status === 'connected',
  );
  // Do not disturb for the mailbox in view (#197): read from the preferences
  // the pollers below already fetch, so it costs no request.
  const pollableIds = pollable.map((candidate) => candidate.id);


  const onMail = location.pathname === '/mail' || location.pathname.startsWith('/mail/');
  const onCalendar =
    location.pathname === '/calendar' || location.pathname.startsWith('/calendar/');
  const onTasks = location.pathname === '/tasks' || location.pathname.startsWith('/tasks/');

  /**
   * `?account=` on a mail, calendar or tasks route: a notification's click
   * names the mailbox its message (or the calendar its event, or the task
   * reminder its task) belongs to, because folder, message, event, list and
   * task ids only mean something inside one account. Consumed once, then
   * dropped from the URL.
   */
  useAccountSearchParam(onMail || onCalendar || onTasks);

  // Keeps the sidebar tree in sync with /mail/:folderId without lifting the
  // mail view's routing state into the shell.
  const selectedFolderId = decodeURIComponent(
    /^\/mail\/([^/]+)/.exec(location.pathname)?.[1] ?? '',
  ) || undefined;

  /**
   * The unified view (ADR 0056) is a route, not a folder, so it is matched on
   * the path rather than looked up in the tree — and the folder tree below it
   * must show nothing selected while it is on.
   */
  const onUnified = selectedFolderId === UNIFIED_SEGMENT;

  /**
   * Compose from anywhere, including from Mail itself.
   *
   * This used to be `navigate('/mail')`, which is a no-op when the user is
   * already on a mail route — so the button did nothing exactly where it is
   * used most, while the `c` shortcut (owned by `MailView`) worked. Asking
   * through the URL instead reaches the composer from Calendar and Contacts as
   * well, and staying on the current mail path keeps the open folder and
   * message rather than throwing the user back to the inbox.
   */
  const openCompose = () => {
    const params = new URLSearchParams(onMail ? searchParams : undefined);
    params.set(COMPOSE_PARAM, COMPOSE_INTENT_NEW);
    navigate({ pathname: onMail ? location.pathname : '/mail', search: params.toString() });
  };

  const query = searchParams.get('q') ?? '';

  /**
   * A route that searches its own records draws its own box, and the shell's
   * steps aside rather than sitting next to it: two boxes on one screen, one
   * searching mail and one searching contacts, is a question nobody should
   * have to answer before typing. Contacts is the one such route; on handheld
   * its app bar already replaces the shell's (contacts.css).
   *
   * Tasks has no search of its own, and the box there read "Search mail" in
   * the Tasks header — a box that looks like it searches the list under it
   * and instead throws you into the Inbox. So it steps aside there too.
   */
  const onRoute = (root: string) =>
    location.pathname === root || location.pathname.startsWith(`${root}/`);
  const ownSearch = onRoute('/contacts') || onRoute('/tasks');

  /**
   * The shell's box searches MAIL, from anywhere.
   *
   * On a mail route it narrows the open folder in place (MailView reads `?q=`).
   * Anywhere else it used to write `?q=` onto the calendar's or Settings' URL,
   * where nothing reads it, so typing did nothing at all. Now the first
   * keystroke there takes the search to the active mailbox's Inbox -- pushed,
   * so Back returns to where the search started -- and the rest refine it in
   * place. The input stays mounted across the move (it belongs to the shell),
   * so focus and the caret do not jump.
   */
  const searchMail = (value: string) => {
    if (onMail) {
      setSearchParams(
        (params) => {
          if (value) params.set('q', value);
          else {
            params.delete('q');
            // The scope belongs to a search; with none, it is noise in the URL
            // that the next search would inherit instead of choosing afresh.
            params.delete(SCOPE_PARAM);
            params.delete(TRASH_PARAM);
          }
          return params;
        },
        { replace: true },
      );
      return;
    }
    if (!value) return;
    navigate(
      {
        pathname: inbox ? `/mail/${encodeURIComponent(inbox.id)}` : '/mail',
        search: new URLSearchParams({ q: value }).toString(),
      },
      // Folders not loaded yet: bare /mail resolves this mailbox's Inbox, and
      // the marker keeps the "open All inboxes" preference from taking the
      // search somewhere else (and dropping it on the way).
      { state: PICKED_MAILBOX_STATE },
    );
  };
  // TODO(plugins): render these under the folder tree once the host can
  // sandbox plugin code. The registry is queried here so the seam is visible.
  const pluginPanels = pluginRegistry.sidebarPanels();

  const activeIndex = accounts.findIndex((candidate) => candidate.id === activeAccount?.id);
  /*
   * The active mailbox's colour on the top bar (#193). Only with a choice of
   * mailbox, and not in "All inboxes", which is every mailbox at once.
   */
  const topbarAccountColor =
    accounts.length > 1 && !onUnified && activeAccount ? accountColorVar(activeAccount, activeIndex) : undefined;

  // From the quota endpoint Settings → Accounts reads (app/quota.ts): the
  // account list never carries figures, so `activeAccount.quota` was only ever
  // set by the mocks.
  const quota = summarizeQuota(useAccountQuota(activeAccount));
  const quotaPercent = quota?.percentText;

  /** What the group is a list of, which is not the same thing with one mailbox. */
  const treeTitle = accounts.length > 1 ? 'Mailboxes' : 'Folders';

  /*
   * Every mailbox, each opening onto its own folders (see MailboxTree). With
   * one mailbox this is exactly the folder tree it always was.
   */
  const folderTree = (
    <MailboxTree
      selectedFolderId={onUnified ? undefined : selectedFolderId}
      unifiedSelected={onUnified}
      onSelectUnified={() => navigate(UNIFIED_PATH)}
      // A `scheduled` / `snoozed` folder opens its view (ADR 0076 §17).
      onSelectFolder={(folder) => navigate(folderPath(folder))}
    />
  );

  /*
   * The drawer's middle section belongs to the route (app/drawer.tsx). On mail,
   * until the mail view registers one of its own, it is the same folder tree
   * the desktop sidebar draws -- the sidebar itself is not rendered on handheld.
   */
  const drawerSection =
    routeDrawerPanel ??
    (onMail ? (
      <div className="drawer__section">
        <div className="drawer__section-title section-title">{treeTitle}</div>
        {folderTree}
      </div>
    ) : null);

  return (
    <div className="shell" data-layout={handheld ? 'handheld' : 'desktop'} data-screen={screen}>
      {pollable.map((candidate) => (
        <AccountNotificationPoller
          key={candidate.id}
          accountId={candidate.id}
          label={candidate.displayName.trim() || candidate.email}
          isPrimary={candidate.isPrimary}
        />
      ))}
      {/* Calendar reminders for every mailbox with a working password, as
          for mail: connected mailboxes have calendars too, and the server
          pushes their reminders. A mailbox that answers 409/501 for its
          calendar is simply skipped by the hook. */}
      {pollable.map((candidate) => (
        <CalendarReminders key={candidate.id} accountId={candidate.id} />
      ))}
      {/* Task reminders, likewise per mailbox: the server pushes them to
          every registered browser with no stored password, and this is the
          fallback for a browser it cannot reach (the desktop app, one never
          registered). A mailbox whose tasks are refused is skipped. */}
      {pollable.map((candidate) => (
        <TaskReminders key={candidate.id} accountId={candidate.id} />
      ))}

      {/* Handheld only. On desktop the page never scrolls, so there is nothing
          to restore, and mounting it would only take `history.scrollRestoration`
          away from the browser. Keyed by pathname: back to the list returns to
          its offset, and every newly opened message starts at its top
          (ADR 0048 §4). */}
      {handheld && <ScrollRestoration getKey={(entry) => entry.pathname} />}

      <SkipLink onMail={onMail} />

      <header
        className={topbarAccountColor ? 'topbar topbar--account' : 'topbar'}
        style={topbarAccountColor ? cssVars({ '--topbar-account-color': topbarAccountColor }) : undefined}
        data-account={topbarAccountColor ? activeAccount?.id : undefined}
      >
        <button
          type="button"
          className="btn btn--icon topbar__menu"
          aria-label="Open navigation"
          aria-haspopup="dialog"
          aria-expanded={drawer.open}
          onClick={() => drawer.setOpen(true)}
        >
          <Icon name="menu" />
        </button>

        <div className="topbar__brand">
          <img className="topbar__logo" src="/icons/icon.svg" alt="" width={24} height={24} />
          <span>OLS Mail</span>
        </div>

        <div className="topbar__search">
          {ownSearch ? null : (
            <>
              <Icon name="search" size={16} className="topbar__search-icon" />
              <input
                id="global-search"
                className="input"
                type="search"
                /*
                 * A syntax nobody can discover is a syntax nobody uses, and this
                 * one is not guessable. The tooltip is the cheapest place to say
                 * it; the no-results state repeats it where someone who just
                 * failed to find something will actually read it.
                 */
                title={
                  'Prefixes: from: to: cc: bcc: subject: body: is:unread|starred in:sent|anywhere\n' +
                  'Dates (month-day-year): date:09-12-25 · date:09-12-25..09-20-25 · after: before: newer_than:7d'
                }
                placeholder={
                  // A phone's search pill cannot hold an email address; on handheld
                  // the account is the avatar at the pill's end instead.
                  handheld
                    ? onUnified
                      ? 'Search all mailboxes'
                      : 'Search mail'
                    : // In the unified view the search reaches EVERY mailbox
                      // (ADR 0056 §6), so naming one here would be a lie about
                      // what is being searched. Which folders of each is the
                      // scope control's to say, under the box.
                      onUnified
                      ? `Search all mailboxes (${accounts.length})`
                      : activeAccount
                        ? `Search ${activeAccount.email}`
                        : 'Search mail'
                }
                value={query}
                aria-label="Search mail"
                onChange={(event) => searchMail(event.target.value)}
              />
              <span className="topbar__search-hint kbd">/</span>
            </>
          )}
          {handheld && activeAccount && (
            <button
              type="button"
              className="topbar__avatar"
              aria-label={`Accounts (${activeAccount.email})`}
              aria-haspopup="dialog"
              onClick={() => setAccountSheetOpen(true)}
            >
              <AccountAvatar account={activeAccount} index={activeIndex} />
            </button>
          )}
        </div>

        <div className="topbar__actions">
          <DndIndicatorGate activeAccountId={activeAccount?.id} accountIds={pollableIds} variant="topbar" />
          <button
            type="button"
            className="btn btn--icon"
            onClick={toggleTheme}
            aria-label={`Theme: ${THEME_LABELS[theme]}. Switch to ${THEME_LABELS[nextTheme(theme)]}`}
            title={`Theme: ${THEME_LABELS[theme]} (click for ${THEME_LABELS[nextTheme(theme)]})`}
          >
            <Icon name={THEME_ICON[theme]} />
          </button>
          {admin && (
            <NavLink to="/admin" className="btn btn--icon" aria-label="Server admin" title="Server admin">
              <Icon name="shield" />
            </NavLink>
          )}
          <NavLink to="/settings" className="btn btn--icon" aria-label="Settings">
            <Icon name="settings" />
          </NavLink>
          {/* Logout is a deliberate teardown, not a navigation: see
              src/session/teardown.ts for what has to disappear with it. */}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void logout()}
            title={account === undefined ? 'Sign out' : `Sign out of ${account.email}`}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* The banner row: one grid row for every app-wide banner, stacked in
          it, so a second one never takes the body's row (shell.css). */}
      <div className="shell__banners">
        <OfflineIndicator />
        <ReauthBanner />
        <CredentialBanner />
      </div>

      <div className="shell__body">
        {/* Not rendered on handheld, rather than hidden by CSS: the drawer draws
            the same folder tree, and a hidden second copy is one more thing for
            the cascade, tests and screen readers to disagree about. That
            disagreement is exactly the bug this replaced (ADR 0048). */}
        {!handheld && (
          <nav className="sidebar" aria-label="Primary">
            <div className="sidebar__compose">
              <button type="button" className="btn btn--primary" onClick={openCompose}>
                <Icon name="compose" size={16} /> Compose
              </button>
            </div>

            <div className="sidebar__scroll">
              <NavLink to="/mail" className="nav-link">
                <Icon name="inbox" size={16} /> Mail
              </NavLink>
              <NavLink to="/calendar" className="nav-link">
                <Icon name="calendar" size={16} /> Calendar
              </NavLink>
              <NavLink to="/contacts" className="nav-link">
                <Icon name="contacts" size={16} /> Contacts
              </NavLink>
              <NavLink to="/tasks" className="nav-link">
                <Icon name="tasks" size={16} /> Tasks
              </NavLink>

              <div className="sidebar__group">
                <div className="sidebar__group-title section-title">{treeTitle}</div>
                {folderTree}
              </div>

              {pluginPanels.length > 0 && (
                <div className="sidebar__group">
                  <div className="sidebar__group-title section-title">Extensions</div>
                  {pluginPanels.map((panel) => (
                    <div className="nav-link" key={panel.id}>
                      {panel.title}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sidebar__footer">
              <AccountSwitcher />
            </div>
          </nav>
        )}

        {/* The `main` query container (shell.css). It exists so the routed
            view's stacking rules have an *ancestor* to query: `.shell__panes`
            used to be its own container, and a container query never matches
            the element it is declared on. */}
        {/* The page's one `main` landmark (#205), and where "Skip to content"
            lands; tabIndex -1 so it can take focus without joining the tab
            order. Views inside use labelled regions, never a second main. */}
        <main className="shell__main" id={MAIN_CONTENT_ID} tabIndex={-1}>
          <ToastSlotContext.Provider value={toastSlot}>
            <Outlet />
          </ToastSlotContext.Provider>
        </main>
      </div>

      <footer className="statusbar">
        <OfflineStatusDot />
        <span className="spacer" />
        {quotaPercent !== undefined && <span>{quotaPercent} of quota used</span>}
        {/* Only where `?` does something: the mail keymap owns it (#343). */}
        {onMail && (
          <span>
            Press <span className="kbd">?</span> for shortcuts
          </span>
        )}
      </footer>

      {handheld && (
        <>
          {hasBottomNav(screen) && (
            <>
              <FabSlot
                fallback={
                  onMail
                    ? { icon: 'compose', label: 'Compose', onClick: openCompose, extended: true }
                    : undefined
                }
              />
              <BottomNav unreadCount={inbox?.unreadCount} inboxId={inbox?.id} />
            </>
          )}

          <Drawer
            open={drawer.open}
            onClose={() => drawer.setOpen(false)}
            label="Navigation"
            header={
              activeAccount && (
                <button
                  type="button"
                  className="drawer__account"
                  aria-haspopup="dialog"
                  aria-label={`Accounts (${activeAccount.email})`}
                  onClick={() => {
                    drawer.setOpen(false);
                    setAccountSheetOpen(true);
                  }}
                >
                  <AccountAvatar account={activeAccount} index={activeIndex} large />
                  <span className="account-switcher__meta">
                    <span className="account-switcher__name truncate">{activeAccount.displayName}</span>
                    <span className="account-switcher__email truncate">{activeAccount.email}</span>
                  </span>
                  <Icon name="chevron-down" size={16} />
                </button>
              )
            }
          >
            {drawerSection}
            {drawerSection && <hr className="drawer__divider" />}
            <DndIndicatorGate activeAccountId={activeAccount?.id} accountIds={pollableIds} variant="drawer" />
            <NavLink to="/settings" className="drawer__item">
              <Icon name="settings" /> Settings
            </NavLink>
            {admin && (
              <NavLink to="/admin" className="drawer__item">
                <Icon name="shield" /> Server admin
              </NavLink>
            )}
            <button type="button" className="drawer__item" onClick={toggleTheme}>
              <Icon name={THEME_ICON[theme]} />
              {`Theme: ${THEME_LABELS[theme]} (switch to ${THEME_LABELS[nextTheme(theme)]})`}
            </button>
            <button
              type="button"
              className="drawer__item"
              onClick={() => {
                // Closed first, like every other row in here. It used to be
                // safe not to: logout always navigated to /login and the whole
                // drawer went with the shell. It no longer always does —
                // unsent mail raises a question instead (ADR 0061) — and a
                // drawer left open covers that question and swallows its
                // buttons on a phone.
                drawer.setOpen(false);
                void logout();
              }}
            >
              {/* The icon set has no sign-out glyph; an empty slot keeps the
                  label on the same column as the rows above. */}
              <span className="drawer__item-icon" aria-hidden="true" /> Sign out
            </button>
            {/* The status bar's content, which has no other home on handheld. */}
            <div className="drawer__status">
              <OfflineStatusDot />
              {quota && (
                <span>
                  {quota.percentText} of {formatBytes(quota.limitBytes)} used
                </span>
              )}
            </div>
          </Drawer>

          <AccountSheet open={accountSheetOpen} onClose={() => setAccountSheetOpen(false)} />
        </>
      )}

      {/* The send undo window lives on the shell, not on the mail view: a
          message held for five seconds must survive a trip to Calendar
          (features/send-undo). Mounting this is also what makes the window
          exist -- with no host, a send goes out at once. */}
      {/* One column for every bottom-of-screen notice, top to bottom: the
          update prompt, the send, the mail view's Undo (portalled into the
          slot). They cannot overlap, whatever their heights (toastStack.ts). */}
      <div className="toast-stack">
        <UpdatePrompt />
        <InstallPromptGate />
        <DraftRecovery />
        <SendUndoSnackbar />
        <div className="toast-stack__slot" ref={setToastSlot} />
      </div>
    </div>
  );
}
